/**
 * AdvancedStrategyPanel
 *
 * Read + EDIT view of a campaign's compiled strategy. Shows the detailed compiled
 * parameters (objective, bidding, budget, geo, placements, audiences) plus the
 * reason behind every decision, and lets the user override the high-impact levers
 * (intent) and Apply them — which persists the edited strategy so it's what
 * publishes. Read fetch never publishes and ignores the feature flag.
 */
import React, { useMemo, useState } from 'react';
import { SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '@/services/api';

interface Decision { field: string; value: unknown; reason: string; confidence: string; locked?: boolean; }

interface AdSetOut {
  name: string; optimization_goal: string; bid_strategy: string;
  bid_amount?: number | null; daily_budget?: number | null;
  targeting?: { interests_semantic?: string[]; [k: string]: unknown };
}

interface GeoIntent { type: string; centers: string[]; radius_km?: number | null; }
interface AudienceIntent {
  geo: GeoIntent; age_intent: string; gender_intent: string;
  interests_semantic: string[]; exclusions_semantic?: string[]; audience_strategy: string;
}
interface AdSetIntent {
  name: string; audience_intent: AudienceIntent;
  bidding_posture: string; placement_posture: string;
}
interface StrategyIntent {
  schema?: string;
  business_read: { primary_outcome: string; [k: string]: unknown };
  campaigns: Array<{ platform: string; ad_sets: AdSetIntent[] }>;
}

interface BudgetHealth {
  status: 'ok' | 'low' | 'critical';
  user_daily: number;
  min_daily: number;
  optimal_daily: number;
  cpa_range: [number, number];
  learning_days: number;
  learning_days_at_budget: number;
  confidence: string;
  basis: string;
  category: string;
  implications: string;
}

interface CompiledPreview {
  available: boolean; reason?: string; currency?: string;
  intent?: StrategyIntent;
  campaign?: { name: string; objective: string; special_ad_categories?: string[] };
  ad_sets?: AdSetOut[];
  decisions?: Decision[]; warnings?: string[]; summary_markdown?: string;
  budget_health?: BudgetHealth;
}

interface Props {
  conversationId?: string;
  /** Canvas mode: a config built from canvas state, compiled via POST (read-only). */
  campaignConfig?: Record<string, unknown>;
  /** Canvas mode with editing: Apply hands the edited strategy (and daily budget) to the canvas,
   *  which saves it where publishing reads it (the canvas state), instead of the stored config. */
  onApply?: (strategyIntent: Record<string, unknown>, dailyBudget?: number) => Promise<unknown>;
  /** Open on first render (e.g. where it's the main thing on screen). */
  defaultOpen?: boolean;
}

const OUTCOMES = ['leads', 'sales', 'traffic', 'awareness', 'engagement'];
const AGES = ['auto', 'working_adults', 'young_adults', 'all'];
const GENDERS = ['all', 'men', 'women'];
const GEO_TYPES = ['radius', 'city', 'region', 'country'];
const PLACEMENTS = [
  {
    value: 'feed_first_quality',
    label: 'Feed-first (recommended)',
    hint: 'Facebook + Instagram Feed only — cleaner, higher-intent clicks. Excludes Stories, Reels, In-stream, and Audience Network.',
  },
  {
    value: 'broad_advantage',
    label: 'Broad reach (Advantage+)',
    hint: "Meta's delivery AI serves across every placement, including Stories/Reels. Best when creative is Reels/Story-native, or for high-volume impulse-purchase campaigns. Needs audience expansion enabled for this brand, or it safely falls back to Feed-first.",
  },
  {
    value: 'feed_only_strict',
    label: 'Feed only (strict)',
    hint: 'Feed only, with no fallback surfaces at all. Use only if you need to avoid every interruption-style placement.',
  },
];
const BIDDINGS = ['maximize_volume_learning', 'efficiency_cost_cap', 'target_roas', 'hard_bid_cap'];
// Labels are deliberately distinct from PLACEMENTS' "Broad reach (Advantage+)" even
// though both use the raw value "broad_advantage" — this one means Advantage+
// *audience* expansion (who sees the ad), not placement/surface expansion (where it
// shows). Same underlying Meta lever name, two different knobs; keep the UI copy
// unambiguous about which is which.
const AUD_STRATEGIES = [
  { value: 'interest_prospecting', label: 'Interest prospecting' },
  { value: 'lookalike_from_customers', label: 'Lookalike from customers' },
  { value: 'retargeting', label: 'Retargeting' },
  { value: 'broad_advantage', label: 'Let Meta expand audience (Advantage+)' },
];
const GOOGLE_CHANNELS = ['SEARCH', 'DISPLAY', 'DEMAND_GEN', 'SHOPPING'];

const money = (minor?: number | null, currency?: string) =>
  minor == null ? '—' : `${(minor / 100).toFixed(2)} ${currency || ''}`.trim();

const select = 'min-h-[40px] rounded-btn border-0 bg-card px-3 text-sm text-ink shadow-ring-line focus:outline-none focus:shadow-ring-ink';
const input = select;
const label = 'mb-1 block text-xs font-semibold text-ink-2';

const PLATFORM_DISPLAY: Record<string, string> = {
  meta: 'Meta',
  google_ads: 'Google Ads',
  pinterest: 'Pinterest',
  twitter: 'Twitter / X',
};
const CANVAS_PLATFORM_KEYS = ['meta', 'google_ads', 'pinterest', 'twitter'];

const CONF_COLORS: Record<string, string> = {
  high: 'bg-ok-soft text-ok',
  medium: 'bg-warn-soft text-warn',
  low: 'bg-inset text-ink-3',
};

function fmtField(raw: string): string {
  return raw.replace(/^ad_set\[\d+\]\./, '').replace(/_/g, ' ');
}

function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  // Prettify internal snake_case posture enums (e.g. interest_prospecting → Interest
  // Prospecting). Only single-token lowercase_snake values — free text, ALL-CAPS API
  // constants, and composite strings ("city: Mumbai, …") are left untouched.
  if (/^[a-z]+(?:_[a-z]+)+$/.test(s)) {
    return s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return s;
}

const DecisionRow: React.FC<{ d: Decision }> = ({ d }) => (
  <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-0.5 py-2 border-t border-line first:border-t-0">
    <span className="text-xs font-medium text-ink-3 capitalize pt-0.5 truncate" title={fmtField(d.field)}>
      {fmtField(d.field)}
    </span>
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-ink">{fmtValue(d.value)}</span>
        {d.confidence && (
          <span className={`rounded px-1.5 py-0.5 text-xs font-semibold uppercase ${CONF_COLORS[d.confidence] || CONF_COLORS.low}`}>
            {d.confidence}
          </span>
        )}
        {d.locked && <span className="rounded bg-brand-soft px-1.5 py-0.5 text-xs font-semibold text-brand-ink">locked</span>}
      </div>
      <p className="mt-0.5 text-xs text-ink-3 leading-snug">{d.reason}</p>
    </div>
  </div>
);

const DecisionsPanel: React.FC<{ decisions: Decision[]; adSets: AdSetOut[] }> = ({ decisions, adSets }) => {
  const campaignDecisions = decisions.filter((d) => d.field.startsWith('campaign.'));
  const adSetCount = adSets.length || Math.max(0, ...decisions.map((d) => {
    const m = d.field.match(/^ad_set\[(\d+)\]/);
    return m ? parseInt(m[1]) + 1 : 0;
  }));

  return (
    <div className="space-y-3">
      {/* Campaign-level decisions */}
      {campaignDecisions.length > 0 && (
        <div className="overflow-hidden rounded-[14px] shadow-ring-line">
          <div className="px-3 py-2 bg-ground">
            <span className="text-label uppercase text-ink-3">Campaign</span>
          </div>
          <div className="px-3">
            {campaignDecisions.map((d, i) => <DecisionRow key={i} d={d} />)}
          </div>
        </div>
      )}

      {/* Per-ad-set decisions */}
      {Array.from({ length: adSetCount }, (_, idx) => {
        const setDecisions = decisions.filter((d) => d.field.startsWith(`ad_set[${idx}].`));
        if (!setDecisions.length) return null;
        const name = adSets[idx]?.name || `Ad set ${idx + 1}`;
        return (
          <div key={idx} className="overflow-hidden rounded-[14px] shadow-ring-line">
            <div className="px-3 py-2 bg-ground">
              <span className="text-label uppercase text-ink-3">{name}</span>
            </div>
            <div className="px-3">
              {setDecisions.map((d, i) => <DecisionRow key={i} d={d} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const AdvancedStrategyPanel: React.FC<Props> = ({ conversationId, campaignConfig, onApply, defaultOpen }) => {
  const [open, setOpen] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const [data, setData] = useState<CompiledPreview | null>(null);

  // Multi-platform tabs: detect platforms from canvas campaignConfig keys.
  const availablePlatforms = useMemo(() => {
    if (!campaignConfig) return [];
    return CANVAS_PLATFORM_KEYS.filter((k) => typeof (campaignConfig as any)[k] === 'object');
  }, [campaignConfig]);
  const isMultiPlatform = availablePlatforms.length > 1;
  const [activePlatform, setActivePlatform] = useState<string>('');

  // Editing state
  const [editing, setEditing] = useState(false);
  const [outcome, setOutcome] = useState('traffic');
  const [budget, setBudget] = useState<number>(0);
  const [adSets, setAdSets] = useState<AdSetIntent[]>([]);
  const [googleChannel, setGoogleChannel] = useState('SEARCH');

  const totalDailyMajor = useMemo(
    () => (data?.ad_sets || []).reduce((s, a) => s + (a.daily_budget || 0), 0) / 100,
    [data],
  );

  const load = async (pf?: string) => {
    setLoading(true); setError(null);
    try {
      // Canvas mode → POST the config built from canvas state (with optional ?platform=);
      // else GET by conversation id.
      const qs = pf ? `?platform=${encodeURIComponent(pf)}` : '';
      const resp = campaignConfig
        ? await apiClient.post(`/api/v1/campaigns/compiled-preview${qs}`, campaignConfig)
        : conversationId
          ? await apiClient.get(`/api/v1/campaigns/${conversationId}/compiled-preview${qs}`)
          : null;
      if (resp) setData(resp.data as CompiledPreview);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err?.response?.data?.detail || 'Could not load the compiled strategy.');
    } finally { setLoading(false); }
  };

  const switchPlatform = (pf: string) => {
    setActivePlatform(pf);
    setData(null);
    setEditing(false);
    void load(pf);
  };

  React.useEffect(() => {
    if (defaultOpen && !open) toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const next = !open; setOpen(next);
    if (next && !data && !loading) {
      const initialPlatform = isMultiPlatform ? availablePlatforms[0] : undefined;
      if (initialPlatform && !activePlatform) setActivePlatform(initialPlatform);
      void load(initialPlatform);
    }
  };

  const detectedPlatform = (data as any)?.platform || 'meta';
  const isGoogle = detectedPlatform === 'google_ads';

  const startEdit = () => {
    const intent = data?.intent;
    if (!intent) return;
    setOutcome(intent.business_read?.primary_outcome || 'traffic');
    setBudget(Math.round(totalDailyMajor) || 0);
    setAdSets(JSON.parse(JSON.stringify(intent.campaigns?.[0]?.ad_sets || [])));
    const ch = (data as any)?.config?.campaign?.channel_type || 'SEARCH';
    setGoogleChannel(ch);
    setApplied(false);
    setEditing(true);
  };

  const buildConfig = () => {
    const platform = detectedPlatform;
    const base: Record<string, unknown> = {
      schema: 'campaign_strategy_intent',
      currency: data?.currency,
      daily_budget: budget || undefined,
      business_read: { ...(data?.intent?.business_read || {}), primary_outcome: outcome },
      campaigns: [{ platform, ad_sets: adSets }],
    };
    if (isGoogle) base.channel_type = googleChannel;
    return base;
  };

  const previewChanges = async () => {
    setSaving(true); setError(null);
    try {
      const resp = await apiClient.post('/api/v1/campaigns/compiled-preview', buildConfig());
      setData(resp.data as CompiledPreview);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err?.response?.data?.detail || 'Preview failed.');
    } finally { setSaving(false); }
  };

  const apply = async () => {
    if (onApply) {
      setSaving(true); setError(null);
      try {
        const cfg = buildConfig();
        await onApply({ schema: cfg.schema, business_read: cfg.business_read, campaigns: cfg.campaigns }, budget || undefined);
        const resp = await apiClient.post('/api/v1/campaigns/compiled-preview', cfg);
        setData(resp.data as CompiledPreview);
        setEditing(false); setApplied(true);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { detail?: string } } };
        setError(err?.response?.data?.detail || 'Apply failed.');
      } finally { setSaving(false); }
      return;
    }
    if (!conversationId) return;
    setSaving(true); setError(null);
    try {
      const cfg = buildConfig();
      const resp = await apiClient.post(`/api/v1/campaigns/${conversationId}/apply-strategy`, {
        strategy_intent: { schema: cfg.schema, business_read: cfg.business_read, campaigns: cfg.campaigns },
        daily_budget: budget || undefined,
      });
      setData(resp.data as CompiledPreview);
      setEditing(false); setApplied(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err?.response?.data?.detail || 'Apply failed.');
    } finally { setSaving(false); }
  };

  const updateAdSet = (i: number, patch: Partial<AdSetIntent>) =>
    setAdSets((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const updateAudience = (i: number, patch: Partial<AudienceIntent>) =>
    setAdSets((prev) => prev.map((a, idx) => (idx === i ? { ...a, audience_intent: { ...a.audience_intent, ...patch } } : a)));
  const updateGeo = (i: number, patch: Partial<GeoIntent>) =>
    setAdSets((prev) => prev.map((a, idx) => (idx === i ? { ...a, audience_intent: { ...a.audience_intent, geo: { ...a.audience_intent.geo, ...patch } } } : a)));

  return (

    <div className="mb-6 rounded-[14px] bg-card shadow-ring-line">
      <button type="button" onClick={toggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left" aria-expanded={open}>
        <span className="flex flex-wrap items-center gap-2 font-display text-[15px] font-bold text-ink">
          <SlidersHorizontal className="h-4 w-4 text-ink-2" />
          Advanced strategy
          <span className="font-sans text-xs font-normal text-ink-3">
            Every parameter, the reason behind it — and edit &amp; apply
          </span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />}
      </button>

      {open && (
        <div className="border-t border-line px-4 py-4">
          {/* Platform tabs — shown only for multi-platform canvas campaigns */}
          {isMultiPlatform && (
            <div className="mb-3 flex gap-1 border-b border-line">
              {availablePlatforms.map((pf) => (
                <button
                  key={pf}
                  type="button"
                  onClick={() => switchPlatform(pf)}
                  className={[
                    '-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors',
                    (activePlatform || availablePlatforms[0]) === pf
                      ? 'border-ink text-ink'
                      : 'border-transparent text-ink-3 hover:text-ink-2',
                  ].join(' ')}
                >
                  {PLATFORM_DISPLAY[pf] ?? pf}
                </button>
              ))}
            </div>
          )}
          {loading && <p className="text-sm text-ink-3">Compiling strategy…</p>}
          {!loading && error && <p className="mb-3 text-sm text-bad">{error}</p>}

          {!loading && data && data.available === false && (
            <p className="text-sm text-ink-3">
              {data.reason || 'Detailed compiled parameters are not available for this campaign yet.'}
            </p>
          )}

          {!loading && data && data.available && (
            <div className="space-y-4">
              {/* Header: objective + edit toggle */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {data.campaign && (
                    <span className="rounded-full bg-brand-soft px-3 py-1 text-xs font-semibold text-brand-ink">
                      {data.campaign.objective}
                    </span>
                  )}
                  {(data.campaign?.special_ad_categories || []).map((c) => (
                    <span key={c} className="rounded-full bg-warn-soft px-3 py-1 text-xs font-semibold text-warn">{c}</span>
                  ))}
                  {applied && <span className="text-xs font-semibold text-ok">Applied ✓</span>}
                </div>
                {/* Edit available for stored campaigns (not canvas-preview mode). Google apply not yet wired. */}
                {!editing && data.intent && (data.ad_sets?.length ?? 0) > 0 && ((conversationId && !campaignConfig) || (onApply && !isGoogle)) && (
                  <button type="button" onClick={startEdit}
                    className="inline-flex min-h-[36px] items-center justify-center rounded-btn bg-brand px-3 font-display text-[13px] font-bold text-brand-on hover:bg-brand-hover disabled:opacity-50">
                    Edit strategy
                  </button>
                )}
              </div>

              {/* ---- EDIT MODE ---- */}
              {editing && (
                <div className="space-y-4 rounded-[14px] bg-ground p-4">
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <label className={label}>Objective</label>
                      <select className={select} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                        {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    {isGoogle && (
                      <div>
                        <label className={label}>Channel type</label>
                        <select className={select} value={googleChannel} onChange={(e) => setGoogleChannel(e.target.value)}>
                          {GOOGLE_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className={label}>Total daily budget ({data.currency})</label>
                      <input className={input} type="number" min={0} value={budget}
                        onChange={(e) => setBudget(Number(e.target.value))} />
                    </div>
                  </div>

                  {adSets.map((a, i) => (
                    <div key={i} className="rounded-[14px] bg-card p-4">
                      <p className="mb-2 text-sm font-semibold text-ink">{a.name}</p>
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                        <div>
                          <label className={label}>Geo type</label>
                          <select className={select} value={a.audience_intent.geo.type}
                            onChange={(e) => updateGeo(i, { type: e.target.value })}>
                            {GEO_TYPES.map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={label}>Locations (comma)</label>
                          <input className={input} value={(a.audience_intent.geo.centers || []).join(', ')}
                            onChange={(e) => updateGeo(i, { centers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                        </div>
                        <div>
                          <label className={label}>Radius km</label>
                          <input className={input} type="number" min={0}
                            value={a.audience_intent.geo.radius_km || 0}
                            onChange={(e) => updateGeo(i, { radius_km: Number(e.target.value) || null })} />
                        </div>
                        <div>
                          <label className={label}>Age</label>
                          <select className={select} value={a.audience_intent.age_intent}
                            onChange={(e) => updateAudience(i, { age_intent: e.target.value })}>
                            {AGES.map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={label}>Gender</label>
                          <select className={select} value={a.audience_intent.gender_intent}
                            onChange={(e) => updateAudience(i, { gender_intent: e.target.value })}>
                            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={label}>Audience</label>
                          <select className={select} value={a.audience_intent.audience_strategy}
                            onChange={(e) => updateAudience(i, { audience_strategy: e.target.value })}>
                            {AUD_STRATEGIES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                          </select>
                        </div>
                        {!isGoogle && (
                          <div className="col-span-2 md:col-span-3">
                            <label className={label}>Placement</label>
                            <select className={select} value={a.placement_posture}
                              onChange={(e) => updateAdSet(i, { placement_posture: e.target.value })}>
                              {PLACEMENTS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                            </select>
                            <p className="mt-1 text-xs text-ink-3 leading-snug">
                              {PLACEMENTS.find((g) => g.value === a.placement_posture)?.hint}
                            </p>
                          </div>
                        )}
                        <div>
                          <label className={label}>Bidding</label>
                          <select className={select} value={a.bidding_posture}
                            onChange={(e) => updateAdSet(i, { bidding_posture: e.target.value })}>
                            {BIDDINGS.map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        <div className="col-span-2 md:col-span-3">
                          <label className={label}>Interests (comma)</label>
                          <input className={`${input} w-full`} value={(a.audience_intent.interests_semantic || []).join(', ')}
                            onChange={(e) => updateAudience(i, { interests_semantic: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                        </div>
                        <div className="col-span-2 md:col-span-3">
                          <label className={label}>Exclude (comma)</label>
                          <input className={`${input} w-full`} value={(a.audience_intent.exclusions_semantic || []).join(', ')}
                            onChange={(e) => updateAudience(i, { exclusions_semantic: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={previewChanges} disabled={saving}
                      className="inline-flex min-h-[36px] items-center justify-center rounded-btn px-3 font-display text-[13px] font-bold text-ink shadow-ring-line hover:shadow-ring-ink disabled:opacity-50">
                      {saving ? 'Working…' : 'Preview changes'}
                    </button>
                    {!isGoogle && (
                      <button type="button" onClick={apply} disabled={saving}
                        className="inline-flex min-h-[36px] items-center justify-center rounded-btn bg-brand px-3 font-display text-[13px] font-bold text-brand-on hover:bg-brand-hover disabled:opacity-50">
                        {saving ? 'Applying…' : 'Apply'}
                      </button>
                    )}
                    <button type="button" onClick={() => setEditing(false)} disabled={saving}
                      className="inline-flex min-h-[36px] items-center justify-center rounded-btn px-3 font-display text-[13px] font-bold text-ink-2 hover:text-ink">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Key levers at a glance */}
              {data.ad_sets && data.ad_sets.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase text-ink-3">
                      <tr>
                        <th className="py-1 pr-4">Ad set</th>
                        <th className="py-1 pr-4">Optimize for</th>
                        <th className="py-1 pr-4">Interests</th>
                        <th className="py-1 pr-4">Bidding</th>
                        <th className="py-1 pr-4">Daily budget</th>
                      </tr>
                    </thead>
                    <tbody className="text-ink-2">
                      {data.ad_sets.map((a, i) => {
                        const interests = a.targeting?.interests_semantic || [];
                        return (
                          <tr key={i} className="border-t border-line">
                            <td className="py-1 pr-4 font-medium">{a.name}</td>
                            <td className="py-1 pr-4">{a.optimization_goal}</td>
                            <td className="py-1 pr-4 text-xs text-ink-3">
                              {interests.length > 0
                                ? interests.join(', ')
                                : <span className="italic">broad (no interests set)</span>}
                            </td>
                            <td className="py-1 pr-4">{a.bid_strategy}{a.bid_amount != null ? ` @ ${money(a.bid_amount, data.currency)}` : ''}</td>
                            <td className="py-1 pr-4">{money(a.daily_budget, data.currency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* The "why" behind every decision — structured, not raw markdown */}
              {data.decisions && data.decisions.length > 0 && (
                <DecisionsPanel decisions={data.decisions} adSets={data.ad_sets || []} />
              )}

              {/* Budget health */}
              {data.budget_health && (
                <div className={[
                  'rounded-lg border p-3',
                  data.budget_health.status === 'critical'
                    ? 'border-bad/40 bg-bad-soft'
                    : data.budget_health.status === 'low'
                      ? 'border-warn/30 bg-warn-soft'
                      : 'border-ok/30 bg-ok-soft',
                ].join(' ')}>
                  <p className={[
                    'mb-2 text-xs font-semibold uppercase',
                    data.budget_health.status === 'critical'
                      ? 'text-bad'
                      : data.budget_health.status === 'low'
                        ? 'text-warn'
                        : 'text-ok',
                  ].join(' ')}>
                    {data.budget_health.status === 'critical' ? '🚨 Budget critical'
                      : data.budget_health.status === 'low' ? '⚠️ Budget low'
                      : '✓ Budget healthy'}
                    <span className="ml-2 font-normal normal-case text-ink-3">
                      ({data.budget_health.confidence} confidence · {data.budget_health.basis.replace(/_/g, ' ')})
                    </span>
                  </p>
                  <div className="mb-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-ink-3">Your daily</p>
                      <p className="font-semibold text-ink">
                        {data.currency} {data.budget_health.user_daily.toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </p>
                    </div>
                    <div>
                      <p className="text-ink-3">Recommended min</p>
                      <p className="font-semibold text-ink">
                        {data.currency} {data.budget_health.min_daily.toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </p>
                    </div>
                    <div>
                      <p className="text-ink-3">Learning phase</p>
                      <p className="font-semibold text-ink">
                        ~{data.budget_health.learning_days_at_budget} days
                        {data.budget_health.learning_days_at_budget > data.budget_health.learning_days && (
                          <span className="ml-1 text-warn">
                            (vs {data.budget_health.learning_days} at optimal)
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-ink-3">Est. CPA range</p>
                      <p className="font-semibold text-ink">
                        {data.currency} {data.budget_health.cpa_range[0].toLocaleString(undefined, {maximumFractionDigits: 0})}–
                        {data.budget_health.cpa_range[1].toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </p>
                    </div>
                    <div>
                      <p className="text-ink-3">Optimal daily</p>
                      <p className="font-semibold text-ink">
                        {data.currency} {data.budget_health.optimal_daily.toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </p>
                    </div>
                    <div>
                      <p className="text-ink-3">Category</p>
                      <p className="font-semibold text-ink capitalize">
                        {data.budget_health.category.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-ink-2">{data.budget_health.implications}</p>
                </div>
              )}

              {/* Warnings & assumptions */}
              {data.warnings && data.warnings.length > 0 && (
                <div className="rounded-lg border border-warn/30 bg-warn-soft p-3">
                  <p className="mb-1 text-xs font-semibold uppercase text-warn">⚠️ Assumptions &amp; warnings</p>
                  <ul className="list-inside list-disc space-y-1 text-sm text-warn">
                    {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <button type="button" onClick={() => setShowRaw((s) => !s)}
                  className="text-xs text-ink-3 underline hover:text-ink-2">
                  {showRaw ? 'Hide raw parameters' : 'Show raw parameters (JSON)'}
                </button>
                {showRaw && (
                  <pre className="mt-2 max-h-80 overflow-auto rounded bg-navy p-3 text-xs text-ink-3">
                    {JSON.stringify({ campaign: data.campaign, ad_sets: data.ad_sets }, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
